import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor10, _descriptor11, _descriptor12, _descriptor13;
import { action, computed, flow, observable, runInAction } from "mobx";
import { CheckoutStep } from "../store";
import { formatNumber, realCommerceRequest, __ } from "../utils";
import { RealCommerceError } from "../exceptions";
import { getAffiliateFromStorage } from "../others";
import { routeLocationPaddleCheckoutPatch, routeLocationPaddleIncompleteCheckoutIsCompleteGet, routeLocationPaddleIncompleteCheckoutPatch, routeLocationPaddleIncompleteCheckoutPost } from "@devowl-wp/api-real-commerce";
import { EProductPlanVisibility } from "@devowl-wp/api-real-commerce";
const RETRY_CHECKOUT_PATCH_RETRIES = 29;
const RETRY_CHECKOUT_PATCH_WAIT = 3000;
let Transaction = (_class = class Transaction {
  get isRecovered() {
    return !!this.recoveredIncompleteCheckout;
  }
  get store() {
    return this.product.store;
  }
  get awaitingPaymentRetryImageAndText() {
    const urlPrefix = `${this.store.rootStore.optionStore.publicUrl}images/delayed-payment/`;
    const {
      awaitingPaymentRetry
    } = this;
    if (awaitingPaymentRetry <= 3) {
      return [undefined, __("We are waiting for the payment confirmation. Be patient for a moment...")];
    } else if (awaitingPaymentRetry <= 6) {
      return [`${urlPrefix}1.jpg`, __("Confirmation of payment takes longer than usual. Please have a little patience...")];
    } else if (awaitingPaymentRetry <= 10) {
      return [`${urlPrefix}2.jpg`, __("We have found the cable through which the payment must. Now it just has to go through...")];
    } else if (awaitingPaymentRetry <= 15) {
      return [`${urlPrefix}3.jpg`, __("Knot in the cable found! Now your payment can finally travel through the vastness of the internet...")];
    } else {
      return [`${urlPrefix}4.jpg`, __("Your payment is on its way. Hopefully it will be confirmed soon...")];
    }
  }

  /**
   * See `CheckoutStep.NotYetProcessedButShowConfirmation`.
   */

  get currentInitialPrice() {
    return typeof this.initialPrice === "number" ? this.initialPrice : this.product.data.productPlan.price[0].initialPrice;
  }
  get currentRecurringPrice() {
    return typeof this.recurringPrice === "number" ? this.recurringPrice : this.product.data.productPlan.price[0].recurringPrice;
  }

  /**
   * Coupon savings in percent. Returns `0` if no price difference.
   */
  get couponSavings() {
    const {
      initialPrice
    } = this;
    const [{
      initialPrice: originalInitialPrice
    }] = this.product.data.productPlan.price;
    if (this.lastAppliedCoupon !== null) {
      const percent = 100 - Math.round(initialPrice / originalInitialPrice * 100);
      return percent;
    }
    return 0;
  }
  get labelPrice() {
    const {
      data: {
        productPlan: {
          price: [{
            currency
          }]
        }
      }
    } = this.product;
    return this.initialPriceTax !== undefined ? __("Price (incl. %s %s VAT)", currency, formatNumber(this.initialPriceTax)) : __("Price (incl. VAT)");
  }
  get labelRecurringPrice() {
    const {
      data: {
        productPlan: {
          price: [{
            currency
          }]
        }
      },
      labelPaymentInterval
    } = this.product;
    return this.currentRecurringPrice && this.currentInitialPrice !== this.currentRecurringPrice ? __("in the first %s, afterwards %s %s%s per %s", labelPaymentInterval, currency, formatNumber(this.currentRecurringPrice), this.recurringPriceTax === undefined ? "" : __(" (incl. %s %s VAT)", currency, formatNumber(this.recurringPriceTax)), labelPaymentInterval) : "";
  }
  constructor(product, recovered) {
    _initializerDefineProperty(this, "product", _descriptor, this);
    _initializerDefineProperty(this, "checkout", _descriptor2, this);
    _initializerDefineProperty(this, "incompleteCheckout", _descriptor3, this);
    _initializerDefineProperty(this, "recoveredIncompleteCheckout", _descriptor4, this);
    _initializerDefineProperty(this, "error", _descriptor5, this);
    /**
     * When doing `applyPayment` and the payment is not yet processed on the eCommerce system,
     * we will retry every x seconds. This variable holds the number of the current attempt.
     */
    _initializerDefineProperty(this, "awaitingPaymentRetry", _descriptor6, this);
    _initializerDefineProperty(this, "notYetProcessedButShowConfirmationText", _descriptor7, this);
    /**
     * A transactions' price can be different to the products' price
     * due to known country and coupon.
     */
    _initializerDefineProperty(this, "initialPrice", _descriptor8, this);
    _initializerDefineProperty(this, "initialPriceTax", _descriptor9, this);
    /**
     * A transactions' price can be different to the products' price
     * due to known country and coupon.
     */
    _initializerDefineProperty(this, "recurringPrice", _descriptor10, this);
    _initializerDefineProperty(this, "recurringPriceTax", _descriptor11, this);
    _initializerDefineProperty(this, "identification", _descriptor12, this);
    /**
     * In most cases, a coupon is added to the transaction via a third-party event as the coupon
     * is inserted in e.g. an `<iframe`. The coupon needs to be synced to our incomplete-checkout
     * and with this observable, we can listen to errors in our reactive frontend.
     */
    _initializerDefineProperty(this, "lastSyncCouponWithIncompleteCheckoutError", _descriptor13, this);
    /**
     * Save the last applied coupon of this transaction. This is necessary so we
     * can automatically safe the applied coupon to our backend or delete it.
     */
    this.lastAppliedCoupon = null;
    /**
     * Apply the coupon and sync it with the incomplete checkout.
     *
     * @param data
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.syncCouponWithIncompleteCheckout = flow(function* (coupon, throwIfError) {
      if (this.lastAppliedCoupon === coupon) {
        // Already synced
        return;
      }
      this.lastAppliedCoupon = coupon;
      try {
        yield realCommerceRequest({
          location: routeLocationPaddleIncompleteCheckoutPatch,
          request: {
            checkout: {
              uuid: this.incompleteCheckout.checkout.uuid,
              coupon
            }
          }
        });
        this.lastSyncCouponWithIncompleteCheckoutError = undefined;
      } catch (e) {
        this.lastSyncCouponWithIncompleteCheckoutError = new RealCommerceError(e);
        if (throwIfError) {
          throw this.lastSyncCouponWithIncompleteCheckoutError;
        }
      }
    });
    /**
     * Remove the current saved VAT information from the current transaction.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.removePersistedVat = flow(function* (throwIfError) {
      try {
        yield realCommerceRequest({
          location: routeLocationPaddleIncompleteCheckoutPatch,
          request: {
            checkout: {
              uuid: this.incompleteCheckout.checkout.uuid
            },
            vatIdRemovedEventThrown: true
          }
        });
      } catch (e) {
        if (throwIfError) {
          throw e;
        }
      }
    });
    /**
     * Set the identification data for this transaction. This must be done AFTER
     * the user has accepted the terms and conditions (including privacy preference, ...).
     * In our case, this is the first step in the checkout form. It also automatically sets
     * the next step in the current checkout of the product.
     *
     * @param data
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.applyIdentification = flow(function* (data, terms, isInvoiceAddressGiven, disableEmailCheck) {
      var _this$recoveredIncomp, _this$recoveredIncomp2;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        data: {
          productPlan: {
            id: planId,
            price: [fullPrice],
            product: {
              id: productId
            },
            visibility
          },
          paddlePayLink
        }
      } = this.product;
      const payLinkUuid = (paddlePayLink === null || paddlePayLink === void 0 ? void 0 : paddlePayLink.uuid) || ((_this$recoveredIncomp = this.recoveredIncompleteCheckout) === null || _this$recoveredIncomp === void 0 ? void 0 : (_this$recoveredIncomp2 = _this$recoveredIncomp.checkout.paddlePayLink) === null || _this$recoveredIncomp2 === void 0 ? void 0 : _this$recoveredIncomp2.uuid);
      try {
        const affiliateCookie = visibility === EProductPlanVisibility.Private || this.isRecovered ? undefined : getAffiliateFromStorage();
        const incompleteCheckout = yield realCommerceRequest({
          location: routeLocationPaddleIncompleteCheckoutPost,
          request: {
            disableEmailCheck,
            checkout: {
              productPlan: {
                id: planId,
                fullPrice
              },
              product: {
                id: productId
              },
              ...(payLinkUuid ? {
                paddlePayLink: {
                  uuid: payLinkUuid
                }
              } : {}),
              ...data,
              ...terms
            },
            isInvoiceAddressGiven,
            ...(!affiliateCookie ? {} : affiliateCookie.type === "devowl" ? {
              affiliateLinkView: {
                hashid: affiliateCookie.hashId
              }
            } :
            // Currently, only `type=devowl` is implemented
            {})
          }
        });
        this.identification = data;
        this.incompleteCheckout = incompleteCheckout;
        this.store.setStep(CheckoutStep.AppliedIdentification);
      } catch (e) {
        throw new RealCommerceError(e);
      }
    });
    /**
     * The payment form got successfully submitted to the payment provider and we can wait for the
     * payment. It automatically retries after x seconds x times until the payment got completed.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    this.applyPayment = flow(function (ecommerceSystemCheckoutId) {
      var _this = this;
      let currentRetry = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      return function* () {
        _this.store.setStep(CheckoutStep.AwaitingPayment);
        _this.awaitingPaymentRetry = currentRetry;
        const lastCall = currentRetry >= RETRY_CHECKOUT_PATCH_RETRIES;
        try {
          const checkout = yield realCommerceRequest({
            location: routeLocationPaddleCheckoutPatch,
            params: {
              retries: currentRetry,
              lastCall
            },
            request: {
              checkout: {
                ecommerceSystemCheckoutId,
                uuid: _this.incompleteCheckout.checkout.uuid
              }
            }
          });
          _this.incompleteCheckout = undefined;
          _this.checkout = checkout;
          _this.store.setStep(CheckoutStep.Paid);
        } catch (e) {
          const error = new RealCommerceError(e);
          if (error.hasErrorCode("CheckoutInPaddleWaitForPaymentConfirmation") && !lastCall) {
            // Wait and try again
            yield new Promise(resolve => setTimeout(resolve, RETRY_CHECKOUT_PATCH_WAIT));
            yield _this.applyPayment(ecommerceSystemCheckoutId, currentRetry + 1);
          } else if (error.hasErrorCode("CheckoutInPaddleNotYetProcessed")) {
            _this.notYetProcessedButShowConfirmationText = error.message;
            _this.store.setStep(CheckoutStep.NotYetProcessedButShowConfirmation);
          } else {
            _this.error = error;
          }
        }
      }();
    });
    runInAction(() => {
      this.product = product;
      this.recoveredIncompleteCheckout = recovered;
    });
  }
  applyNewPrices(_ref) {
    let {
      initialPrice,
      initialPriceTax,
      recurringPrice,
      recurringPriceTax,
      coupon
    } = _ref;
    this.initialPrice = initialPrice;
    this.initialPriceTax = initialPriceTax;
    this.recurringPrice = recurringPrice;
    this.recurringPriceTax = recurringPriceTax;
    this.syncCouponWithIncompleteCheckout(coupon);
  }
  /**
   * Fetch incomplete order status and return the external eCommerce checkout id.
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  async fetchIsIncompleteCheckoutComplete() {
    try {
      return await realCommerceRequest({
        location: routeLocationPaddleIncompleteCheckoutIsCompleteGet,
        params: {
          uuid: this.incompleteCheckout.checkout.uuid
        }
      });
    } catch (e) {
      throw new RealCommerceError(e);
    }
  }

  /**
   * Load external files. For example the Paddle JS SDK. Also, setup the
   * given SDK wit the `product.ecommerceVendorId`.
   */
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "product", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "checkout", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "incompleteCheckout", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "recoveredIncompleteCheckout", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "isRecovered", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isRecovered"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "store", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "store"), _class.prototype), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "error", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "awaitingPaymentRetry", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "awaitingPaymentRetryImageAndText", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "awaitingPaymentRetryImageAndText"), _class.prototype), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "notYetProcessedButShowConfirmationText", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "initialPrice", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "initialPriceTax", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "recurringPrice", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "recurringPriceTax", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "identification", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "lastSyncCouponWithIncompleteCheckoutError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "currentInitialPrice", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "currentInitialPrice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "currentRecurringPrice", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "currentRecurringPrice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "couponSavings", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "couponSavings"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "labelPrice", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labelPrice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "labelRecurringPrice", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labelRecurringPrice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "applyNewPrices", [action], Object.getOwnPropertyDescriptor(_class.prototype, "applyNewPrices"), _class.prototype)), _class);
export { Transaction, RETRY_CHECKOUT_PATCH_WAIT };